import {
  Button,
  Checkbox,
  ConfirmationBox,
  ContextMenu,
  Icon,
  Labeled,
  NumericInput,
  Select,
  SelectProps,
  Tabs,
  TextInput,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import {
  DatasetStyleRuleCondition,
  TFormFieldName,
} from '@infotech-soe/metaschema';
import classNames from 'classnames';
import React, {
  DragEvent,
  memo,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { IntlFormatters, useIntl } from 'react-intl';

import { isDroppable } from '../../../../helpers';
import { CategoryOpenMode } from '../../../../models/category/category';
import {
  DatasetPresetRule,
  TotalsRuleFront,
} from '../../../../models/user-config/DisplayModeConfig';
import CommandViewModel, {
  CommandListEntryType,
} from '../../../../services/commands/CommandViewModel';
import { DEFAULT_ROWS_LIMIT_IN_DATASET } from '../../../../services/dataset/display-mode/DisplayModeDatasetService';
import { useContextMenu } from '../../../hooks/useContextMenu';
import { getCaptionText } from '../../Caption';
import { ButtonOption, createOptions } from '../../commands/CommandsToolbar';
import configFormReducer, {
  ConfigFormActionType,
} from '../utils/ConfigFormReducer';
import { getVacantName, importPreset } from '../utils/preset';

import ConfigToolbar, { SupportedToolbarActions } from './ConfigToolbar';
import FormulasForm from './FormulasForm';
import messages from './messages';
import PresetNameEditor from './RenamePresetForm';
import RuleEditor from './RuleEditor';
import useStyles from './styles';
import { ConfigFormProps } from './types';

const DRAGGABLE_EVENT_NAME = 'config-draggable-event';

type ActionsRow =
  | {
      kind: 'command';
      name: string;
      icon: ButtonOption['icon'];
      label: string;
      level: number;
    }
  | { kind: 'group'; name: string; label: string; level: number };

const buildActionsRows = (
  commands: CommandViewModel[],
  commandOptions: Map<string, ButtonOption>,
  formatMessage: IntlFormatters['formatMessage'],
  level = 0
): ActionsRow[] => {
  const rows: ActionsRow[] = [];
  for (const command of commands) {
    if (command.type === CommandListEntryType.COMMAND) {
      const opt = commandOptions.get(command.name);
      if (opt) {
        rows.push({
          kind: 'command',
          name: command.name,
          icon: opt.icon,
          label: opt.label,
          level,
        });
      }
    } else if (command.type === CommandListEntryType.GROUP) {
      const children = buildActionsRows(
        command.children,
        commandOptions,
        formatMessage,
        level + 1
      );
      if (children.some(r => r.kind === 'command')) {
        const label = getCaptionText(command.caption, formatMessage);
        rows.push({ kind: 'group', name: command.name, label, level });
        rows.push(...children);
      }
    }
  }
  return rows;
};

const ConfigForm: React.FC<ConfigFormProps> = ({
  open,
  title,
  openMode,
  hasConditionsHook,
  config: propsConfig,
  orderedColumns,
  locale,
  commands,
  onCloseForm,
  onError,
  onUpdateConfig,
  columns,
  recordViewMap,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [
    {
      config,
      isFormPresetOpened,
      order,
      presetActionConfirmation,
      shouldUpdateDatasetOnSave,
      totalsField,
    },
    localDispatch,
  ] = useReducer(configFormReducer, {
    config: propsConfig,
    isFormPresetOpened: false,
    order: orderedColumns,
    presetActionConfirmation: {
      presetName: '',
      handler: () => {
        // do nothing
      },
    },
    shouldUpdateDatasetOnSave: false,
  });

  const { preset, currentPreset } = config;

  const {
    rules,
    pinned = 0,
    locateNode = false,
    showDictionaryCondition = false,
    totals,
    toolbarCommands,
    hasRowsLimitInDataset = false,
    valueOfRowsLimitInDataset = DEFAULT_ROWS_LIMIT_IN_DATASET,
    caseSensitive = true,
    recordView,
    hideZeroValues = false,
    defaultCommand,
  } = preset[currentPreset];

  const actionsRows = useMemo(() => {
    const options = createOptions(commands, formatMessage);
    const commandOptions = new Map(
      options
        .filter((o): o is ButtonOption => 'name' in o && o.name !== 'settings')
        .map(o => [o.name, o])
    );
    return buildActionsRows(commands, commandOptions, formatMessage);
  }, [commands, formatMessage]);

  const [selectedAction, setSelectedAction] = useState<string | undefined>();
  const actionsContextMenu = useContextMenu();
  const actionsContextMenuItems = useMemo(
    () => [
      {
        type: 'listitem',
        value: 'useDefault',
        label: formatMessage(messages.useDefault),
        disabled: defaultCommand === selectedAction,
      },
    ],
    [defaultCommand, selectedAction, formatMessage]
  );
  const onActionsContextMenuOpen = useCallback(
    (event: MouseEvent<HTMLDivElement>, name: string) => {
      setSelectedAction(name);
      const { clientX, clientY } = event;

      actionsContextMenu.setPositionX(clientX);
      actionsContextMenu.setPositionY(clientY);
      setTimeout(() => {
        actionsContextMenu.open();
      });
    },
    [actionsContextMenu]
  );
  const onActionsContextMenuClick = (item: string) => {
    if (item === 'useDefault') {
      localDispatch([
        ConfigFormActionType.UPDATE_CONFIG_PRESET,
        { defaultCommand: selectedAction },
      ]);
    }
    actionsContextMenu.close();
  };
  const onActionsContextMenuClose = () => {
    actionsContextMenu.close();
  };
  const onCommandChange = useCallback(
    (type: 'toolbar' | 'menu', name: string, value: boolean) => {
      localDispatch([
        ConfigFormActionType.UPDATE_CONFIG_PRESET_TOOLBAR_COMMANDS,
        {
          commandName: name,
          value:
            type === 'toolbar'
              ? { hideInToolbar: !value }
              : { hideInMenu: !value },
        },
      ]);
    },
    []
  );

  const commandNames = useMemo(
    () => actionsRows.filter(r => r.kind === 'command').map(r => r.name),
    [actionsRows]
  );

  const allVisible = useMemo(() => {
    let toolbar = commandNames.length > 0;
    let menu = commandNames.length > 0;
    for (const name of commandNames) {
      const cfg = toolbarCommands?.[name];
      if (cfg?.hideInToolbar ?? cfg?.hide) toolbar = false;
      if (cfg?.hideInMenu ?? cfg?.hide) menu = false;
      if (!toolbar && !menu) break;
    }
    return { toolbar, menu };
  }, [commandNames, toolbarCommands]);

  const onToggleAll = useCallback(
    (type: 'toolbar' | 'menu') => {
      const showAll =
        type === 'toolbar' ? !allVisible.toolbar : !allVisible.menu;
      commandNames.forEach(name => {
        localDispatch([
          ConfigFormActionType.UPDATE_CONFIG_PRESET_TOOLBAR_COMMANDS,
          {
            commandName: name,
            value:
              type === 'toolbar'
                ? { hideInToolbar: !showAll }
                : { hideInMenu: !showAll },
          },
        ]);
      });
    },
    [commandNames, allVisible]
  );

  const onSetDefaultCommand = useCallback(
    (name: string) => {
      if (defaultCommand !== name) {
        localDispatch([
          ConfigFormActionType.UPDATE_CONFIG_PRESET,
          { defaultCommand: name },
        ]);
      }
    },
    [defaultCommand]
  );

  const maxPossiblePinnedColumnsCount = useMemo(
    () => order.length - 1,
    [order]
  );

  const ruleTabs = useMemo(
    () =>
      orderedColumns.map(column => {
        const ruleTitle = rules[column.field].title;
        return {
          label: ruleTitle?.[locale] || column.title,
          column,
        };
      }),
    [locale, orderedColumns, rules]
  );

  const recordViewOptions: SelectProps['options'] | undefined = useMemo(
    () =>
      recordViewMap
        ? Object.entries(recordViewMap).map(([name, record]) => ({
            label: record.label,
            type: 'option',
            value: name,
          }))
        : undefined,
    [recordViewMap]
  );

  const handleOnSelectedToolbar = async (action: SupportedToolbarActions) => {
    switch (action) {
      case ConfigFormActionType.ADD_PRESET:
        localDispatch([
          ConfigFormActionType.SET_PRESET_ACTION_CONFIRMATION,
          {
            presetName: getVacantName(
              Object.keys(preset),
              formatMessage(messages.preset)
            ),
            handler: name => localDispatch([action, { name, orderedColumns }]),
          },
        ]);
        break;
      case ConfigFormActionType.COPY_PRESET:
        localDispatch([
          ConfigFormActionType.SET_PRESET_ACTION_CONFIRMATION,
          {
            presetName: getVacantName(
              Object.keys(preset),
              formatMessage(messages.copyOf) + ' ' + currentPreset
            ),
            handler: name => localDispatch([action, name]),
          },
        ]);
        break;
      case ConfigFormActionType.DELETE_PRESET:
        localDispatch([ConfigFormActionType.DELETE_PRESET]);
        break;
      case ConfigFormActionType.RENAME_PRESET:
        localDispatch([
          ConfigFormActionType.SET_PRESET_ACTION_CONFIRMATION,
          {
            presetName: currentPreset,
            handler: name => localDispatch([action, name]),
          },
        ]);
        break;
      case ConfigFormActionType.IMPORT_PRESET: {
        try {
          const newState = await importPreset(config, orderedColumns);
          onUpdateConfig(newState);
          localDispatch([action, newState]);
        } catch (e) {
          onError(e as Error);
        }
        break;
      }
      case ConfigFormActionType.EXPORT_PRESET:
        localDispatch([action]);
        break;
      case ConfigFormActionType.RESET_PRESET:
        localDispatch([action, columns]);
        break;
      default:
        throw new Error(`Action handler ${action} is not implemented`);
    }
  };

  const onConfirmRule = useCallback(
    (ruleName: string, rule: DatasetPresetRule) => {
      localDispatch([
        ConfigFormActionType.UPDATE_CONFIG_PRESET_RULE,
        { ruleName, rule },
      ]);
    },
    []
  );

  const onConfirmConditions = useCallback(
    (key: string, ruleConditions: DatasetStyleRuleCondition[]) => {
      onConfirmRule(key, {
        ...rules[key],
        conditions: ruleConditions,
      });
    },
    [onConfirmRule, rules]
  );

  const onChangeRules = (key: string) => (value: string) => {
    onConfirmRule(key, {
      ...rules[key],
      title: rules[key].title
        ? { ...rules[key].title, [locale]: value }
        : { [locale]: value },
    });
  };

  const onChangeVisibility = (key: string) => (value: boolean) => {
    onConfirmRule(key, { ...rules[key], visibility: value });
  };

  const onSave = useCallback(() => {
    onUpdateConfig(
      { ...config, columnsOrder: order.map(column => column.field) },
      shouldUpdateDatasetOnSave
    );
    onCloseForm();
  }, [config, shouldUpdateDatasetOnSave, onCloseForm, onUpdateConfig, order]);

  const onDragLeave = (event: SyntheticEvent) => {
    event.currentTarget.classList.remove('config__column--dragover');
  };

  const onDragStart = (event: DragEvent) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer?.setData(
      `${DRAGGABLE_EVENT_NAME}/data-index`,
      (event.currentTarget as HTMLElement)?.dataset.index as string
    );
  };

  const allowDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isDroppable(event, DRAGGABLE_EVENT_NAME)) {
      event.currentTarget.classList.add('config__column--dragover');
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDroppable(event, DRAGGABLE_EVENT_NAME)) {
      return;
    }

    const prevIndex = Number(
      event.dataTransfer?.getData(`${DRAGGABLE_EVENT_NAME}/data-index`)
    );
    const currIndex = Number(event.currentTarget?.dataset.index);

    const { field } = order[prevIndex];

    const newOrder = [...order].filter(column => column.field !== field);
    newOrder.splice(currIndex, 0, order[prevIndex]);
    localDispatch([ConfigFormActionType.SET_ORDER, newOrder]);

    document
      .querySelector('.config__column--dragover')
      ?.classList.remove('config__column--dragover');
  };

  const handleSubmitTotalsConfig = useCallback(
    (
      field: TFormFieldName,
      modifiedTotals?: TotalsRuleFront[],
      updateDataset?: boolean
    ) => {
      localDispatch([
        ConfigFormActionType.SET_TOTALS_CONFIG,
        {
          field,
          modifiedTotals,
          shouldUpdateDatasetOnSave: updateDataset,
        },
      ]);
    },
    []
  );

  const handleToggleFormulasForm = useCallback(
    (fieldName?: string) => () => {
      localDispatch([ConfigFormActionType.SET_TOTALS_FIELD, fieldName]);
    },
    []
  );

  if (!orderedColumns.length) {
    return null;
  }

  const renderShowDictionaryCondition = () =>
    openMode === CategoryOpenMode.DICTIONARY && hasConditionsHook ? (
      <div className="row gutter-sm items-center m-pa-md">
        <Checkbox
          focusable
          label={formatMessage(messages.showDictionaryCondition)}
          name="showDictionaryCondition"
          value={showDictionaryCondition}
          onChange={(value: boolean) =>
            localDispatch([
              ConfigFormActionType.UPDATE_CONFIG_PRESET,
              { showDictionaryCondition: value },
            ])
          }
        />
      </div>
    ) : null;

  const renderReviewRecord = () =>
    recordViewOptions ? (
      <div className="m-pa-md">
        <Labeled
          childColumns={6}
          label={formatMessage(messages.recordView)}
          labelPosition="left"
          labelVerticalAlign="center"
        >
          <Select
            name="recordView"
            options={recordViewOptions}
            onChange={value =>
              localDispatch([
                ConfigFormActionType.UPDATE_CONFIG_PRESET,
                { recordView: value || undefined },
              ])
            }
            value={recordView || null}
          />
        </Labeled>
      </div>
    ) : null;

  return (
    <ConfirmationBox
      className={classes.dialog}
      open={open}
      submitForbidden={
        pinned < 0 ||
        valueOfRowsLimitInDataset < 0 ||
        pinned > maxPossiblePinnedColumnsCount
      }
      title={title}
      noCloseButton={false}
      onCancel={onCloseForm}
      onClose={onCloseForm}
      onConfirm={onSave}
      header={
        <ConfigToolbar
          className={classes.toolbar}
          current={currentPreset}
          onChange={(value: string) => {
            localDispatch([
              ConfigFormActionType.UPDATE_CONFIG,
              { currentPreset: value },
            ]);
          }}
          onAction={handleOnSelectedToolbar}
          presets={Object.keys(preset)}
        />
      }
    >
      <Tabs syncHeight className={classes.configTabs}>
        <Tabs.Pane
          name={formatMessage(messages.columns)}
          className={classes.columnsTab}
        >
          <div className={classes.columnsWrapper}>
            {order.map((column, index) => {
              const { field, domain } = column;
              const rule = rules[field];
              return (
                <div
                  key={field}
                  className={classes.columns}
                  draggable
                  onDragOver={allowDrop}
                  onDragLeave={onDragLeave}
                  onDragStart={onDragStart}
                  data-index={index}
                  onDrop={onDrop}
                >
                  <TextInput
                    placeholder={column.title}
                    value={rule.title?.[locale]}
                    onChange={onChangeRules(field)}
                  />
                  {domain.type === 'number' && (
                    <Tooltip label={formatMessage(messages.totals)}>
                      <Button
                        className="col-auto m-ml-sm"
                        name="totals"
                        focusable
                        onClick={handleToggleFormulasForm(field)}
                        icon="sum"
                        variant={totals?.[field] ? 'filled' : 'borderless'}
                      />
                    </Tooltip>
                  )}
                  <Icon name="hamburger" className={classes.draggableButton} />
                  <Checkbox
                    className="col-auto m-mr-md"
                    name="visibility"
                    value={rule.visibility}
                    focusable
                    onChange={onChangeVisibility(field)}
                  />
                </div>
              );
            })}
            {totalsField && (
              <FormulasForm
                open={!!totalsField}
                totals={totals}
                field={totalsField}
                onSubmit={handleSubmitTotalsConfig}
                onCancel={handleToggleFormulasForm()}
              />
            )}
          </div>
          <div className={classes.fixedColumns}>
            <Labeled
              label={formatMessage(messages.pinnedColumnsTitle)}
              labelPosition="left"
              labelVerticalAlign="center"
              labelHorizontalAlign="start"
              childColumns={3}
            >
              <NumericInput
                name="pinned"
                value={pinned}
                min={0}
                max={maxPossiblePinnedColumnsCount}
                invalid={pinned > maxPossiblePinnedColumnsCount}
                onChange={value => {
                  localDispatch([
                    ConfigFormActionType.UPDATE_CONFIG_PRESET,
                    { pinned: value ?? 0 },
                  ]);
                }}
              />
            </Labeled>
          </div>
          <PresetNameEditor
            open={isFormPresetOpened}
            value={presetActionConfirmation.presetName}
            validate={(value: string) => !Object.keys(preset).includes(value)}
            onCancel={() =>
              localDispatch([
                ConfigFormActionType.SET_IS_FORM_PRESET_OPENED,
                false,
              ])
            }
            // eslint-disable-next-line react/jsx-handler-names
            onConfirm={presetActionConfirmation.handler}
          />
        </Tabs.Pane>
        <Tabs.Pane
          name={formatMessage(messages.design)}
          className={classes.designTab}
        >
          <Tabs buttonsWidth={200} sideTabs syncHeight>
            {ruleTabs.map(({ column, label }) => {
              const rule = rules[column.field];
              const hasRule = rule.conditions.some(
                ({
                  color: { backgroundColor, color },
                  value: {
                    textBold,
                    textItalic,
                    textUnderline,
                    textLineThrough,
                  },
                }) =>
                  backgroundColor ||
                  color ||
                  textBold ||
                  textItalic ||
                  textUnderline ||
                  textLineThrough
              );
              return (
                <Tabs.Pane
                  name={column.field}
                  key={column.field}
                  label={label}
                  buttonClassName={hasRule ? classes.tabButton : undefined}
                >
                  <RuleEditor
                    rule={rule}
                    column={column}
                    onConfirm={onConfirmConditions}
                  />
                </Tabs.Pane>
              );
            })}
          </Tabs>
        </Tabs.Pane>
        <Tabs.Pane name={formatMessage(messages.actions)}>
          <table className={classes.actionsTable}>
            <thead>
              <tr>
                <th className={classes.actionsThLeft} />
                <th className={classes.actionsThCenter}>
                  <div className={classes.actionsThContent}>
                    <span>{formatMessage(messages.inToolbar)}</span>
                    <Tooltip
                      label={formatMessage(
                        allVisible.toolbar === true
                          ? messages.hideInToolbarHint
                          : messages.showInToolbarHint
                      )}
                    >
                      <span>
                        <Checkbox
                          name="toolbar-all"
                          value={allVisible.toolbar}
                          focusable
                          onChange={() => onToggleAll('toolbar')}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </th>
                <th className={classes.actionsThCenter}>
                  <div className={classes.actionsThContent}>
                    <span>{formatMessage(messages.inMenu)}</span>
                    <Tooltip
                      label={formatMessage(
                        allVisible.menu === true
                          ? messages.hideInMenuHint
                          : messages.showInMenuHint
                      )}
                    >
                      <span>
                        <Checkbox
                          name="menu-all"
                          value={allVisible.menu}
                          focusable
                          onChange={() => onToggleAll('menu')}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {actionsRows.map(row =>
                row.kind === 'group' ? (
                  <tr key={`group-${row.name}`} className={classes.actionsRow}>
                    <td
                      className={classes.actionsGroupTd}
                      colSpan={3}
                      style={{ paddingLeft: `${12 + row.level * 16}px` }}
                    >
                      {row.label}
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={row.name}
                    className={classNames(classes.actionsRow, {
                      'text-bold': row.name === defaultCommand,
                    })}
                  >
                    <td
                      className={classes.actionsTd}
                      style={{
                        paddingLeft: `${12 + row.level * 16}px`,
                      }}
                      onContextMenu={event =>
                        onActionsContextMenuOpen(event, row.name)
                      }
                      onDoubleClick={() => onSetDefaultCommand(row.name)}
                    >
                      <span>
                        {row.icon && <Icon name={row.icon} size="1.2em" />}
                      </span>
                      <span className="m-ml-sm">{row.label}</span>
                    </td>
                    <td className={classes.actionsTdCenter}>
                      <Checkbox
                        name={`toolbar-${row.name}`}
                        value={
                          !(
                            toolbarCommands?.[row.name]?.hideInToolbar ??
                            toolbarCommands?.[row.name]?.hide
                          )
                        }
                        focusable
                        onChange={(value: boolean) =>
                          onCommandChange('toolbar', row.name, value)
                        }
                      />
                    </td>
                    <td className={classes.actionsTdCenter}>
                      <Checkbox
                        name={`menu-${row.name}`}
                        value={
                          !(
                            toolbarCommands?.[row.name]?.hideInMenu ??
                            toolbarCommands?.[row.name]?.hide
                          )
                        }
                        focusable
                        onChange={(value: boolean) =>
                          onCommandChange('menu', row.name, value)
                        }
                      />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
          <ContextMenu
            x={actionsContextMenu.positionX}
            y={actionsContextMenu.positionY}
            open={actionsContextMenu.isOpen}
            onClick={onActionsContextMenuClick}
            onOpen={onActionsContextMenuClose}
            options={actionsContextMenuItems}
          />
        </Tabs.Pane>
        <Tabs.Pane name={formatMessage(messages.other)}>
          <div className="col gutter-md m-pt-md m-px-md">
            {renderReviewRecord()}
            <div className="row gutter-sm items-center m-pa-md">
              <Checkbox
                label={formatMessage(messages.caseInsensitive)}
                name="caseInsensitive"
                value={!caseSensitive}
                focusable
                onChange={(value: boolean) => {
                  localDispatch([
                    ConfigFormActionType.UPDATE_CONFIG_PRESET,
                    { caseSensitive: !value },
                  ]);
                  localDispatch([
                    ConfigFormActionType.SET_SHOULD_UPDATE_DATASET_ON_SAVE,
                    true,
                  ]);
                }}
              />
            </div>
            <div className="row gutter-sm items-center m-pa-md">
              <Checkbox
                label={formatMessage(messages.locateNode)}
                name="locateNode"
                value={locateNode}
                focusable
                onChange={(value: boolean) =>
                  localDispatch([
                    ConfigFormActionType.UPDATE_CONFIG_PRESET,
                    { locateNode: value },
                  ])
                }
              />
            </div>
            {renderShowDictionaryCondition()}
            <div className="row gutter-sm items-center m-pa-md">
              <Checkbox
                label={formatMessage(messages.hideZeroValues)}
                name="hideZeroValues"
                value={hideZeroValues}
                focusable
                onChange={(value: boolean) =>
                  localDispatch([
                    ConfigFormActionType.UPDATE_CONFIG_PRESET,
                    { hideZeroValues: value },
                  ])
                }
              />
            </div>
            <div className="row gutter-sm items-center m-pa-md">
              <div className="col-6">
                <Checkbox
                  label={formatMessage(messages.rowsLimitInDataset)}
                  name="hasRowsLimitInDataset"
                  value={hasRowsLimitInDataset}
                  focusable
                  onChange={(value: boolean) =>
                    localDispatch([
                      ConfigFormActionType.UPDATE_CONFIG_PRESET,
                      { hasRowsLimitInDataset: value },
                    ])
                  }
                />
              </div>
              <div className="col-6">
                <NumericInput
                  disabled={!hasRowsLimitInDataset}
                  name="valueOfRowsLimitInDataset"
                  value={valueOfRowsLimitInDataset}
                  min={0}
                  onChange={(value: number | null) =>
                    localDispatch([
                      ConfigFormActionType.UPDATE_CONFIG_PRESET,
                      { valueOfRowsLimitInDataset: value ?? undefined },
                    ])
                  }
                />
              </div>
            </div>
          </div>
        </Tabs.Pane>
      </Tabs>
    </ConfirmationBox>
  );
};

export default memo(ConfigForm);
